import React from 'react';
import useGameStore from '../../store/gameStore';
import { TrendingUp } from 'lucide-react';

/**
 * SCD Lot 3 — Décomposition pédagogique du trafic du jour.
 * Affichée sur le Dashboard (compact) et en tête de la page Marketing.
 * Chaque facteur est un multiplicateur : ×1.00 = neutre.
 */
const FACTEURS = [
  { key: 'weekend', label: 'Week-end', explique: 'Samedi/dimanche : +25 % de flux piéton.' },
  { key: 'equipe', label: 'Équipe', explique: 'Vendeurs, merchandiser, options de planning (page Équipe).' },
  { key: 'leviers', label: 'Leviers marketing', explique: 'Communication, réseaux, vitrine, fidélité… (page Marketing).' },
  { key: 'avis', label: 'Avis en ligne', explique: 'Note moyenne des avis : de ×0,85 (1★) à ×1,15 (5★).' },
  { key: 'segments', label: 'Segments clients', explique: 'Réaction des 4 segments à vos prix, votre service et votre choix.' },
  { key: 'promo', label: 'Promotion', explique: 'Boost de trafic, réduit par l\'habitude si les promos sont trop fréquentes.' },
  { key: 'periode', label: 'Période commerciale', explique: 'Soldes, Black Friday, Noël… (calendrier page Marketing).' },
  { key: 'evenements', label: 'Événements', explique: 'Aléas en cours : concurrence, météo, incidents magasin…' },
  { key: 'localisation', label: 'Localisation du magasin', explique: 'Coefficients trafic × panier × concurrence de la localisation choisie au tunnel d\'ouverture.' },
  { key: 'reseau', label: 'Réseau de magasins', explique: 'Contribution des magasins secondaires (chaque ouverture ajoute son trafic).' },
  { key: 'ameliorations', label: 'Améliorations du magasin', explique: 'Effet trafic de vos investissements : vitrine, PLV, click & collect… (écran Évolution du magasin).' },
  { key: 'chantier', label: 'Travaux en cours', explique: 'Gros chantier en cours : magasin partiellement fermé (-30 % de trafic).' },
  { key: 'caisse', label: 'Attente en caisse', explique: 'Trop d\'acheteurs pour vos caisses → clients qui renoncent.' },
];

const chipClass = (v) => v > 1.001
  ? 'bg-emerald-600/15 text-emerald-400 border-emerald-700/50'
  : v < 0.999
    ? 'bg-red-600/15 text-red-400 border-red-800/50'
    : 'bg-ink-700/60 text-slate-400 border-ink-600';

export default function TraficDecompoCard({ compact = false }) {
  const retail = useGameStore(s => s.retail);
  const d = retail?.stats?.decompoTrafic;
  if (!retail || !d) return null;

  return (
    <div className="surface-card overflow-hidden" data-testid="trafic-decompo-card">
      <div className="px-4 py-2.5 border-b border-ink-600 flex items-center justify-between">
        <h3 className="font-display text-xs font-bold text-foreground tracking-tight uppercase flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-accent" /> Pourquoi ce trafic hier ?
        </h3>
        <span className="text-[11px] font-mono font-bold text-slate-200" data-testid="trafic-total-mult">
          Total ×{(d.total ?? 1).toFixed(2)}
          {d.periodeNom && <span className="ml-2 text-accent">{d.periodeNom}</span>}
        </span>
      </div>
      <div className={`p-3 flex flex-wrap gap-2 ${compact ? '' : 'md:gap-3'}`}>
        {FACTEURS.map(f => {
          const v = d[f.key];
          if (v == null) return null;
          if (compact && Math.abs(v - 1) < 0.005) return null;
          return (
            <span
              key={f.key}
              title={f.explique}
              className={`inline-flex items-center gap-1.5 border rounded-full px-2.5 py-1 text-[11px] font-mono cursor-help ${chipClass(v)}`}
              data-testid={`facteur-${f.key}`}
            >
              <span className="font-sans font-medium">{f.label}</span> ×{Number(v).toFixed(2)}
            </span>
          );
        })}
        {compact && FACTEURS.every(f => d[f.key] == null || Math.abs(d[f.key] - 1) < 0.005) && (
          <span className="text-[11px] text-slate-500">Journée neutre : aucun facteur ne modifie le trafic de base.</span>
        )}
      </div>
      {!compact && (
        <p className="px-4 pb-3 text-[11px] text-slate-500 leading-snug">
          Demande du jour = flux de la zone × captation du secteur × <b>tous ces multiplicateurs</b> × élasticité-prix par famille.
          Survolez chaque facteur pour comprendre comment l'améliorer.
        </p>
      )}
    </div>
  );
}
